import React from 'react'
import moment from 'moment'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider
} from '@mui/material'

const ViewMedicationModal = ({
  selectedMedication,
  setSelectedMedication,
  isOpen,
  setIsOpen
}) => {
  const handleClose = () => {
    setIsOpen(false)
    setSelectedMedication(null)
  }

  if (!selectedMedication) return null

  const med = selectedMedication.resource
  const dosageInstruction = med.dosageInstruction?.[0] || {}
  const timing = dosageInstruction.timing?.repeat || {}
  const doseQuantity = dosageInstruction.doseAndRate?.[0]?.doseQuantity || {}

  // Format days of week if present
  const getDaysOfWeek = () => {
    if (!timing.dayOfWeek) return 'Not specified'

    const dayMap = {
      mon: 'Monday',
      tue: 'Tuesday',
      wed: 'Wednesday',
      thu: 'Thursday',
      fri: 'Friday',
      sat: 'Saturday',
      sun: 'Sunday'
    }

    return timing.dayOfWeek.map((day) => dayMap[day] || day).join(', ')
  }

  // Format time of day if present
  const getTimesOfDay = () => {
    if (!timing.timeOfDay) return 'Not specified'

    return timing.timeOfDay
      .map((time) => (time.includes(':') ? time.substring(0, 5) : time))
      .join(', ')
  }

  // Format period unit
  const getPeriodUnit = () => {
    const unitMap = {
      d: 'daily',
      wk: 'weekly',
      mo: 'monthly'
    }

    return unitMap[timing.periodUnit] || timing.periodUnit || 'Not specified'
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Medication Details</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom>
            {med.medicationCodeableConcept?.text || 'Unnamed Medication'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            <strong>Status:</strong>{' '}
            {med.status
              ? med.status.charAt(0).toUpperCase() + med.status.slice(1)
              : 'Unknown'}
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            <strong>Prescribed:</strong>{' '}
            {med.authoredOn
              ? moment(med.authoredOn).format('MMM Do YYYY')
              : 'Unknown date'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Dosage Instructions
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Dosage:</strong> {doseQuantity.value || 'Not specified'}{' '}
            {doseQuantity.unit || 'unit(s)'}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Frequency:</strong> {timing.frequency || 'Not specified'}{' '}
            time(s) {getPeriodUnit()}
          </Typography>

          {timing.periodUnit === 'wk' && (
            <Typography variant="body1" gutterBottom>
              <strong>Days:</strong> {getDaysOfWeek()}
            </Typography>
          )}

          <Typography variant="body1" gutterBottom>
            <strong>Times:</strong> {getTimesOfDay()}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Route:</strong>{' '}
            {dosageInstruction.route?.coding?.[0]?.display || 'Not specified'}
          </Typography>

          {dosageInstruction.text && (
            <>
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Instructions
              </Typography>

              <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                {dosageInstruction.text}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ViewMedicationModal
